import {
  BudgetRecipientEnum,
  IBudgetApiPayload,
  IBudgetFormData,
  IBudgetItem,
  IBudgetService,
} from "../components/budgetsForm/createAndEdit/types";
import { BudgetHistory } from "../components/budgetsForm/types";
import mockBudgetItems from "../mock/budget/budgetItems.json";
import ApiService from "../services/api";
import type {
  IBudgetApiResponse,
  IBudgetServiceApiResponse,
  IBudgetListItem,
  IBudgetsListResponse,
  IBudgetVersion,
  IBudgetVersionsResponse,
  IServiceOption,
  IBudgetServiceItem,
  IBudgetServiceResponse,
  IBudgetServicesResponse,
  BudgetServiceCategory,
  IBudgetServicePackageSummary,
  IBudgetServicePackagesListResponse,
  IBudgetServicePackageLineApi,
  IBudgetServiceItemOption,
  IBudgetServiceItemsResponse,
  IApprovalPayload,
  IConsultantApprovalPayload,
  IFactoryApprovalPayload,
  ApprovalRejectRole,
  IBudgetAttachment,
  IBudgetKanbanFilters,
  IBudgetKanbanResponse,
  IBudgetKanbanSummary,
} from "../models/budget";

import { FAILURE_MODES_LIST, IFailureModeOption } from "../constants/budget";
import { BudgetStatusFilterEnum } from "../enums/budgetStatus";
import {
  parseHourMeter,
  parseNumber,
} from "../components/budgetsForm/createAndEdit/utils/budgetCalculations";
import { formatStringToNumbersOnly } from "../utils/stringUtils";

export type {
  IBudgetListItem,
  IBudgetsListResponse,
  IBudgetVersion,
  IBudgetVersionsResponse,
  IServiceOption,
  IBudgetServiceItem,
  IBudgetServiceResponse,
  IBudgetServicesResponse,
  BudgetServiceCategory,
  IBudgetServicePackageSummary,
  IBudgetServicePackagesListResponse,
  IBudgetServicePackageLineApi,
  IBudgetServiceItemOption,
  IBudgetServiceItemsResponse,
  IApprovalPayload,
  IConsultantApprovalPayload,
  IFactoryApprovalPayload,
  ApprovalRejectRole,
  IFailureModeOption,
};

export const fetchBudgetById = async (
  budgetId: string,
  occurrenceId: string,
  versionNumber: number = 1
): Promise<IBudgetFormData | undefined> => {
  try {
    const apiService = new ApiService();
    const response = await apiService.get<IBudgetApiResponse>(
      `/budgets/${budgetId}/versions/${versionNumber}`
    );

    if (response.status >= 200 && response.status < 300 && response.data) {
      const apiData = response.data;
      const mapService = (
        service: IBudgetServiceApiResponse,
        serviceIndex: number
      ): IBudgetService => {
        const mappedItems: IBudgetItem[] = service.serviceItems.map(
          (item, itemIndex) => {
            const grossValue = item.value * item.quantity;
            let discountPercent = 0;

            if (grossValue > 0 && item.discount > 0) {
              discountPercent =
                Math.round((item.discount / grossValue) * 100 * 100) / 100;
              discountPercent = Math.max(0, Math.min(100, discountPercent));
            }

            const discountPercentString =
              discountPercent % 1 === 0
                ? `${Math.round(discountPercent)}%`
                : `${discountPercent.toFixed(2).replace(".", ",")}%`;

            const isCovered = item.isCovered === true;
            return {
              ref: item.ref,
              item: item.item,
              description: item.description,
              quantity: item.quantity.toString(),
              value: item.value,
              discount: discountPercentString,
              subtotal: isCovered ? 0 : item.subtotal,
              isCovered,
              notCoveredReason: item.notCoveredReason,
              availableIn: item.availableIn,
            };
          }
        );

        const observationValue =
          service.observation &&
          typeof service.observation === "string" &&
          service.observation.trim() !== ""
            ? service.observation
            : undefined;

        const mappedService = {
          ref: service.ref,
          serviceId: service.id || service.ref,
          serviceName: service.name,
          serviceType: service.serviceType,
          items: mappedItems,
          observation: observationValue,
          subtotal: mappedItems.reduce((sum, item) => sum + item.subtotal, 0),
          clientPercentage: service.percentages.customer,
          volkstotalPercentage: service.percentages.volkstok,
          dealershipPercentage: service.percentages.dealership,
          warrantyPercentage: service.percentages.warranty,
          isExternalService: service.isExternalService ?? false,
          percentages: service.percentages,
          failureModeId: service.failureMode,
        };

        return mappedService;
      };

      const mappedServices: IBudgetService[] = apiData.services.map(
        (service, index) => mapService(service, index)
      );
      const mappedProposalServices: IBudgetService[] =
        apiData.proposalServices?.map((service, index) =>
          mapService(service, index)
        ) || [];

      const vehicleData = {
        protocolNumber: apiData.vehicle.protocolNumber || undefined,
        chassis: apiData.vehicle.chassis || undefined,
        licensePlate: apiData.vehicle.licensePlate || undefined,
        year: apiData.vehicle.year?.toString() || undefined,
        model: apiData.vehicle.model || undefined,
        odometer: apiData.vehicle.odometer?.toString() || undefined,
        hourmeter:
          apiData.vehicle.hourMeter !== null &&
          apiData.vehicle.hourMeter !== undefined
            ? apiData.vehicle.hourMeter.toString()
            : undefined,
        contractNumber: apiData.vehicle.contractNumber || undefined,
      };

      const clientData = {
        name: apiData.customer.name || undefined,
        email: apiData.customer.email || undefined,
        phone: apiData.customer.phone || undefined,
      };

      const finalProposalServices =
        mappedProposalServices.length > 0
          ? mappedProposalServices
          : JSON.parse(JSON.stringify(mappedServices)); // Deep copy

      const budgetFormData: IBudgetFormData = {
        occurrenceId: apiData.vehicle.protocolNumber || occurrenceId,
        number: apiData.number,
        budgetId: budgetId,
        budgetCreatedBy: apiData.budgetCreatedBy || undefined,
        vehicleData,
        clientData,
        services: mappedServices,
        proposalServices: finalProposalServices,
        summary: {
          subtotal: apiData.subtotal,
          discounts: apiData.discount,
          totalToPay: apiData.netTotal,
        },
        observation: apiData.observation || undefined,
        counterProposalValue: apiData.counterProposalValue ?? undefined,
        counterProposalObs: apiData.counterProposalObservation || undefined,
        counterProposalAcceptedAt:
          apiData.counterProposalApprovedAt || undefined,
        origin: apiData.origin || undefined,
        originUserName: apiData.originUserName || undefined,
        recipient: apiData.recipient || undefined,
        status: (apiData as any).budgetStatus || undefined,
        versionNumber: versionNumber,
        username: apiData.username || undefined,
        consultant: apiData.consultant || undefined,
        subscriptionBalanceToApply: apiData.subscriptionBalanceApplied ?? undefined,
      };

      return budgetFormData;
    }

    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const fetchBudgetsList = async (
  occurrenceId: string,
  sortBy?: string,
  sortDirection?: "asc" | "desc",
  page?: number,
  pageSize?: number
): Promise<IBudgetsListResponse> => {
  const apiService = new ApiService();
  try {
    const params = new URLSearchParams();
    if (sortBy) {
      params.append("sortBy", sortBy);
    }
    if (sortDirection) {
      params.append("sortDirection", sortDirection);
    }
    if (page !== undefined) {
      params.append("page", String(page));
    }
    if (pageSize !== undefined) {
      params.append("pageSize", String(pageSize));
    }

    const queryString = params.toString();
    const url = queryString
      ? `/occurrence/${occurrenceId}/budgets?${queryString}`
      : `/occurrence/${occurrenceId}/budgets`;

    const response = await apiService.get<any>(url);
    if (response.status === 200 && response.data) {
      return response.data as IBudgetsListResponse;
    }
    return {
      budgets: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: pageSize || 50,
      last: true,
      first: true,
    };
  } catch (error) {
    return {
      budgets: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: pageSize || 50,
      last: true,
      first: true,
    };
  }
};

export const fetchBudgetHistory = async (
  budgetId: string,
  sortBy?: string,
  sortDirection?: "asc" | "desc",
  page?: number,
  pageSize?: number,
  includeVersionsOfOtherRecipients?: boolean
): Promise<{ data: BudgetHistory[]; total: number; totalPages: number }> => {
  const apiService = new ApiService();
  try {
    const params = new URLSearchParams();
    if (sortBy) {
      params.append("sortBy", sortBy);
    }
    if (sortDirection) {
      params.append("sortDirection", sortDirection);
    }
    if (page !== undefined) {
      params.append("page", String(page));
    }
    if (pageSize !== undefined) {
      params.append("pageSize", String(pageSize));
    }
    if (includeVersionsOfOtherRecipients) {
      params.append("includeVersionsOfOtherRecipients", "true");
    }
    const queryString = params.toString();
    const url = queryString
      ? `/budgets/${budgetId}/versions?${queryString}`
      : `/budgets/${budgetId}/versions`;

    const response = await apiService.get<any>(url);

    if (response.status === 200 && response.data) {
      const versionsResponse = response.data as IBudgetVersionsResponse;
      return {
        data: versionsResponse.versions.map((version) => ({
          id: version.uuid || version.budgetUuid,
          originUserName: version.originUserName,
          profile: version.profile,
          user: version.username,
          budgetUser: version.username,
          version: version.versionNumber,
          versionDate: new Date(version.createdAt).toLocaleDateString("pt-BR"),
          status: version.status,
          consultant: version.consultant,
        })),
        total: versionsResponse.totalElements,
        totalPages: versionsResponse.totalPages,
      };
    }
    return {
      data: [],
      total: 0,
      totalPages: 0,
    };
  } catch (error) {
    return {
      data: [],
      total: 0,
      totalPages: 0,
    };
  }
};

const normalizeBudgetPackageItemsPayload = (
  data: unknown
): IBudgetServicePackageLineApi[] => {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data as IBudgetServicePackageLineApi[];
  }
  if (
    typeof data === "object" &&
    data !== null &&
    "items" in data &&
    Array.isArray((data as { items: unknown }).items)
  ) {
    return (data as { items: IBudgetServicePackageLineApi[] }).items;
  }
  return [];
};

const appendBudgetPackageVehicleParams = (
  params: URLSearchParams,
  chassis: string,
  odometer?: string,
  hourMeter?: string
) => {
  params.append("chassis", chassis ?? "");
  const odometerDigits = odometer?.replaceAll(/[^\d]/g, "") ?? "";
  const parsedOdometer = parseNumber(odometerDigits);
  const odometerInt = Number.isFinite(parsedOdometer) ? Math.round(parsedOdometer) : 0;
  const hourMeterSeconds = hourMeter?.trim() ? (parseHourMeter(hourMeter) ?? 0) : 0;
  const hourMeterInt = Number.isFinite(hourMeterSeconds) ? Math.round(hourMeterSeconds / 3600) : 0;
  params.append("odometer", String(odometerInt));
  params.append("hourMeter", String(hourMeterInt));
};

export const fetchBudgetServicePackages = async (
  serviceType: BudgetServiceCategory,
  chassis: string,
  term: string,
  page: number = 1,
  limit: number = 20,
  odometer?: string,
  hourMeter?: string
): Promise<IServiceOption[]> => {
  try {
    const apiService = new ApiService();
    const params = new URLSearchParams();
    appendBudgetPackageVehicleParams(params, chassis, odometer, hourMeter);
    const trimmed = term.trim();
    if (trimmed) {
      params.append("searchTerm", trimmed);
    }
    params.append("page", String(page));
    params.append("limit", String(limit));

    const response = await apiService.get<IBudgetServicePackagesListResponse>(
      `/budgets/services/${serviceType}?${params.toString()}`
    );

    if (response.status < 400 && response.data?.items) {
      return response.data.items.map((item) => ({
        id: item.id,
        label: item.name,
      }));
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const fetchBudgetServicePackageItems = async (
  serviceType: BudgetServiceCategory,
  packageId: string,
  chassis: string,
  odometer?: string,
  hourMeter?: string
): Promise<IBudgetServicePackageLineApi[]> => {
  try {
    const apiService = new ApiService();
    const params = new URLSearchParams();
    appendBudgetPackageVehicleParams(params, chassis, odometer, hourMeter);
    const response = await apiService.get<unknown>(
      `/budgets/services/${serviceType}/${encodeURIComponent(packageId)}/items?${params.toString()}`
    );

    if (response.status < 400 && response.data !== undefined) {
      return normalizeBudgetPackageItemsPayload(response.data);
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const fetchBudgetFailureModes = async (
  serviceId?: string,
  searchTerm?: string,
  limit?: number
): Promise<IFailureModeOption[]> => {
  try {
    let failureModes = [...FAILURE_MODES_LIST];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      failureModes = failureModes.filter(
        (mode) =>
          mode.label.toLowerCase().includes(lowerSearchTerm) ||
          mode.id.toLowerCase().includes(lowerSearchTerm)
      );
    }

    if (limit) {
      failureModes = failureModes.slice(0, limit);
    }

    return failureModes;
  } catch (error) {
    return [];
  }
};

export const fetchBudgetItemsByServiceAndFailureMode = async (
  serviceId: string,
  failureModeId?: string
): Promise<IBudgetItem[]> => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const itemsMap = mockBudgetItems as Record<
      string,
      Record<string, IBudgetItem[]>
    >;

    let mappedServiceId = serviceId;
    if (serviceId === "CORRETIVA") {
      mappedServiceId = "revisao-corretiva-1";
    } else if (serviceId === "DESGASTE") {
      mappedServiceId = "revisao-desgaste-1";
    }

    if (serviceId === "DESGASTE") {
      const serviceItems = itemsMap[mappedServiceId];
      if (serviceItems && typeof serviceItems === "object") {
        const allItems: IBudgetItem[] = [];
        Object.values(serviceItems).forEach((items) => {
          if (Array.isArray(items)) {
            allItems.push(...items);
          }
        });
        return allItems;
      }
      return [];
    }

    if (!failureModeId) {
      return [];
    }

    const failureModeMap: Record<string, string> = {
      "1.0": "sistema-motor",
      "6.0": "sistema-freio",
    };

    let mappedFailureModeId = failureModeId;
    if (failureModeMap[failureModeId]) {
      mappedFailureModeId = failureModeMap[failureModeId];
    }

    const serviceItems = itemsMap[mappedServiceId];
    const items = serviceItems?.[mappedFailureModeId] || [];

    return items;
  } catch (error) {
    console.error("[fetchBudgetItemsByServiceAndFailureMode] erro:", error);
    return [];
  }
};

export const uploadBudgetFiles = async (
  budgetId: string,
  files: File[]
): Promise<boolean> => {
  if (files.length === 0) {
    return true;
  }

  try {
    const apiService = new ApiService();

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      const url = `/attachment/budget/${budgetId}/upload`;

      const response = await apiService.post<void>(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status < 200 || response.status >= 300) {
        return false;
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const downloadBudgetAttachment = async (
  budgetId: string,
  filename: string
): Promise<any> => {
  try {
    const apiService = new ApiService();
    return await apiService.getAndDownloadFile(
      `/attachment/budget/${budgetId}/${filename}/download`
    );
  } catch (error) {
    console.error("[downloadBudgetAttachment] erro ao baixar anexo", error);
    throw error;
  }
};

export const fetchBudgetAttachments = async (
  budgetId: string
): Promise<IBudgetAttachment[]> => {
  try {
    const apiService = new ApiService();
    const url = `/attachment/budget/${budgetId}`;

    const response = await apiService.get<IBudgetAttachment[]>(url);

    if (response.status < 200 || response.status >= 300) {
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("[fetchBudgetAttachments] erro ao buscar anexos", error);
    return [];
  }
};

export const fetchBudgetServiceItems = async (
  searchTerm?: string,
  limit?: number,
  page?: number
): Promise<
  Array<{
    id: string;
    label: string;
    value: number;
    itemData?: IBudgetServiceItemOption;
  }>
> => {
  try {
    const apiService = new ApiService();

    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim() !== "") {
      params.append("searchTerm", searchTerm.trim());
    }
    params.append("page", String(page ?? 1));
    params.append("limit", String(limit ?? 50));

    const response = await apiService.get<IBudgetServiceItemsResponse>(
      `/budgets/services/items?${params.toString()}`
    );

    if (response.status < 400 && response.data) {
      return response.data.items.map((item) => ({
        id: item.item,
        label: item.item,
        ref: item.ref || item.item,
        value: item.value || 0,
        itemData: {
          ...item,
          isCovered: item.isCovered === true,
          notCoveredReason: item.notCoveredReason,
          availableIn: item.availableIn,
        },
      }));
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const fetchBudgetUserProfile = async (): Promise<
  BudgetRecipientEnum | undefined
> => {
  try {
    const apiService = new ApiService();
    const response = await apiService.get<any>("/users/role");

    if (response.status >= 200 && response.status < 300 && response.data) {
      const roleFromApi = response.data.role || response.data;

      if (!roleFromApi || typeof roleFromApi !== "string") {
        return undefined;
      }

      // Convert factory to tower
      if (roleFromApi === "FACTORY" || roleFromApi === "TOWER") {
        return BudgetRecipientEnum.TOWER;
      }

      if (roleFromApi === "CONSULTANT" || roleFromApi === "GROUP_CONSULTANT") {
        return BudgetRecipientEnum.CONSULTANT;
      }

      return roleFromApi as BudgetRecipientEnum;
    }

    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const createBudget = async (
  payload: IBudgetApiPayload
): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.post<any>("/budgets", payload);
    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao criar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao criar orçamento" };
  }
};

export const approveBudgetAsManager = async (
  budgetId: string,
  versionNumber: number,
  counterProposalValue?: number,
  counterProposalObs?: string,
  subscriptionBalanceToApply?: number
): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const payload: any = {
      observation: "",
      counterProposalObservation: counterProposalObs || "",
      counterProposalValue:
        counterProposalValue !== undefined && counterProposalValue !== null
          ? counterProposalValue
          : "",
      subscriptionBalanceToApply: subscriptionBalanceToApply ?? 0
    };

    const response = await apiService.patch<any>(
      `/budgets/${budgetId}/versions/${versionNumber}/manager/approve`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao aprovar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao aprovar orçamento" };
  }
};

export const rejectBudgetAsManager = async (
  budgetId: string,
  versionNumber: number,
  counterProposalValue?: number,
  counterProposalObs?: string
): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const payload: any = {
      observation: "",
      counterProposalObservation: counterProposalObs || "",
      counterProposalValue:
        counterProposalValue !== undefined && counterProposalValue !== null
          ? counterProposalValue
          : "",
    };

    const response = await apiService.patch<any>(
      `/budgets/${budgetId}/versions/${versionNumber}/manager/reject`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao recusar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao recusar orçamento" };
  }
};

export const approveBudget = async (
  budgetId: string,
  versionNumber: number,
  role: ApprovalRejectRole,
  payload: IApprovalPayload
): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.patch<any>(
      `/budgets/${budgetId}/versions/${versionNumber}/${role}/approve`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao aprovar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao aprovar orçamento" };
  }
};

export const updateBudget = async (
  budgetId: string,
  versionNumber: number,
  role: ApprovalRejectRole,
  payload: IApprovalPayload
): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.patch<any>(
      `/budgets/${budgetId}/versions/${versionNumber}/${role}/update`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao atualizar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao atualizar orçamento" };
  }
};

export const rejectBudget = async (formData: {
  budgetId: string;
  versionNumber: number;
  role: ApprovalRejectRole;
  rejectionObservation?: string;
  services?: IApprovalPayload["services"];
  proposalServices?: IApprovalPayload["proposalServices"];
  subtotal?: number;
  discount?: number;
  netTotal?: number;
  counterProposalObservation?: string;
  counterProposalValue?: number;
}): Promise<{ data?: any; error?: string }> => {
  const apiService = new ApiService();
  try {
    const payload: any = {
      observation: formData.rejectionObservation || "",
    };

    if (formData.counterProposalObservation !== undefined) {
      payload.counterProposalObservation =
        formData.counterProposalObservation || "";
    }
    if (
      formData.counterProposalValue !== undefined &&
      formData.counterProposalValue !== null
    ) {
      payload.counterProposalValue = formData.counterProposalValue;
    }

    if (formData.services && formData.services.length > 0) {
      payload.services = formData.services;
      if (formData.proposalServices !== undefined) {
        payload.proposalServices = formData.proposalServices;
      }
      if (formData.subtotal !== undefined && formData.subtotal !== null) {
        payload.subtotal = formData.subtotal;
      }
      if (formData.discount !== undefined && formData.discount !== null) {
        payload.discount = formData.discount;
      }
      if (formData.netTotal !== undefined && formData.netTotal !== null) {
        payload.netTotal = formData.netTotal;
      }
    }
    const response = await apiService.patch<any>(
      `/budgets/${formData.budgetId}/versions/${formData.versionNumber}/${formData.role}/reject`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      return { data: response.data };
    } else {
      return { error: "Erro ao recusar orçamento" };
    }
  } catch (error: any) {
    if (error?.response?.status === 422) {
      return { error: error.response.data?.message || "Erro de validação" };
    }
    return { error: "Erro ao recusar orçamento" };
  }
};

const getBudgetStatusFilterKey = (statusValue: string): string | null => {
  const enumKeys = Object.keys(BudgetStatusFilterEnum) as Array<
    keyof typeof BudgetStatusFilterEnum
  >;
  const foundKey = enumKeys.find(
    (key) => BudgetStatusFilterEnum[key] === statusValue
  );
  return foundKey || null;
};

export const fetchBudgetsKanban = async (
  filters: IBudgetKanbanFilters = {}
): Promise<IBudgetKanbanResponse> => {
  const apiService = new ApiService();
  try {
    const params = new URLSearchParams();

    if (filters.page !== undefined) {
      params.append("page", String(filters.page));
    }
    if (filters.limit !== undefined) {
      params.append("limit", String(filters.limit));
    }
    if (filters.kanbanStep) {
      params.append("kanbanStep", filters.kanbanStep);
    }
    if (filters.status) {
      const statusKey = getBudgetStatusFilterKey(filters.status);
      if (statusKey) {
        params.append("status", statusKey);
      }
    }
    if (filters.startUpdatedAt) {
      params.append("startUpdatedAt", filters.startUpdatedAt);
    }
    if (filters.endUpdatedAt) {
      params.append("endUpdatedAt", filters.endUpdatedAt);
    }
    if (filters.chassis) {
      params.append("chassis", filters.chassis);
    }
    if (filters.recipient) {
      params.append("recipient", filters.recipient);
    }
    if (filters.board) {
      params.append("board", filters.board);
    }

    const queryString = params.toString();
    const url = queryString ? `/budgets?${queryString}` : `/budgets`;

    const response = await apiService.get<any>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      const data = response.data;
      return {
        budgets: data.budgets || data.items || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        currentPage: data.currentPage || filters.page || 1,
        pageSize: data.pageSize || filters.limit || 50,
      };
    }

    return {
      budgets: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: filters.page || 1,
      pageSize: filters.limit || 50,
    };
  } catch (error) {
    console.error("[fetchBudgetsKanban] erro:", error);
    return {
      budgets: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: filters.page || 1,
      pageSize: filters.limit || 50,
    };
  }
};

export const fetchBudgetsKanbanSummary = async (
  filters: Omit<IBudgetKanbanFilters, "page" | "limit">
): Promise<IBudgetKanbanSummary> => {
  try {
    const apiService = new ApiService();
    const params = new URLSearchParams();

    if (filters.startUpdatedAt) {
      params.append("startUpdatedAt", filters.startUpdatedAt);
    }
    if (filters.endUpdatedAt) {
      params.append("endUpdatedAt", filters.endUpdatedAt);
    }
    if (filters.chassis) {
      params.append("chassis", filters.chassis);
    }
    if (filters.recipient) {
      params.append("recipient", filters.recipient);
    }
    if (filters.status) {
      const statusKey = getBudgetStatusFilterKey(filters.status);
      if (statusKey) {
        params.append("status", statusKey);
      }
    }
    if (filters.board) {
      params.append("board", filters.board);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/budgets/status-counts?${queryString}`
      : `/budgets/status-counts`;

    const response = await apiService.get<any>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      const data = response.data;

      const statusCounts: Record<string, number> = {
        enviados: 0,
        alterados: 0,
        contraproposta: 0,
        aceito: 0,
        recusado: 0,
        concluido: 0,
      };

      if (data.counts && Array.isArray(data.counts)) {
        data.counts.forEach((item: any) => {
          if (item.status && typeof item.count === "number") {
            switch (item.status) {
              case "SENT":
                statusCounts.enviados = item.count;
                break;
              case "UPDATED":
                statusCounts.alterados = item.count;
                break;
              case "COUNTER_PROPOSED":
                statusCounts.contraproposta = item.count;
                break;
              case "APPROVED_BY_FACTORY":
                statusCounts.aceito += item.count;
                break;
              case "REJECTED":
                statusCounts.recusado = item.count;
                break;
              case "APPROVED":
                statusCounts.concluido = item.count;
                break;
            }
          }
        });
      }

      return {
        enviados: statusCounts.enviados,
        alterados: statusCounts.alterados,
        contraproposta: statusCounts.contraproposta,
        aceito: statusCounts.aceito,
        recusado: statusCounts.recusado,
        concluido: statusCounts.concluido,
      };
    }

    return {
      enviados: 0,
      alterados: 0,
      contraproposta: 0,
      aceito: 0,
      recusado: 0,
      concluido: 0,
    };
  } catch (error) {
    return {
      enviados: 0,
      alterados: 0,
      contraproposta: 0,
      aceito: 0,
      recusado: 0,
      concluido: 0,
    };
  }
};

export const exportBudgets = async (
  filters: IBudgetKanbanFilters = {}
): Promise<{ errorMessage: string | null, successMessage: string | null }> => {
  const apiService = new ApiService();

  const toLocalDateTimeString = (value: string, endOfDay: boolean = false): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const pad = (n: number) => String(n).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  try {
    const params = new URLSearchParams();
    
    params.append("kanbanStep", 'ACCEPTED');
    params.append("status", 'APPROVED');

    if (filters.startUpdatedAt) {
      params.append("startUpdatedAt", toLocalDateTimeString(filters.startUpdatedAt));
    }
    if (filters.endUpdatedAt) {
      params.append("endUpdatedAt", toLocalDateTimeString(filters.endUpdatedAt, true));
    }
    if (filters.chassis) {
      params.append("chassis", filters.chassis);
    }
    if (filters.recipient) {
      params.append("recipient", filters.recipient);
    }
    if (filters.board) {
      params.append("board", filters.board);
    }

    const queryString = params.toString();
    const url = queryString ? `/budgets/export?${queryString}` : `/budgets/export`;

    const response = await apiService.get<Blob>(url, {
      responseType: 'blob',
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      const contentDisposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];

      let filename = "orcamentos-aprovados.xlsx";
      if (typeof contentDisposition === "string") {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        const rawFilename = utf8Match?.[1] || plainMatch?.[1];
        if (rawFilename) {
          filename = decodeURIComponent(rawFilename);
        }
      }

      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data as BlobPart]);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { errorMessage: null, successMessage: "Orçamentos baixados com sucesso!" };
    } else {
      return { errorMessage: "Erro ao baixar orçamentos", successMessage: null };
    }
  } catch (error) {
    return { errorMessage: `Erro ao baixar orçamentos: ${error}`, successMessage: null };
  }
};
